import { isSessionEnded, type ConsoleApi } from '@central/api';
import { theme } from '@central/ui';
import { MantineProvider } from '@mantine/core';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  type DefaultOptions,
} from '@tanstack/react-query';
import { RouterProvider, type RouterHistory } from '@tanstack/react-router';
import { useState } from 'react';
import { ApiContext, createConsoleApi } from './api';
import { createAppRouter } from './router';

export interface AppProps {
  api?: ConsoleApi;
  /** Merged over the app's query defaults. */
  queryDefaults?: DefaultOptions;
  history?: RouterHistory;
}

export function App(props: AppProps) {
  const [deps] = useState(() => {
    const api = props.api ?? createConsoleApi();
    // A 401 unauthenticated from any query or mutation means the session is
    // gone (idle expiry, sign-out elsewhere): drop every cached answer, me
    // included, so the next guarded route asks the server again, and go to
    // sign in. Other 401 codes (a wrong code or password) are answers.
    const onError = (e: Error) => {
      if (!isSessionEnded(e)) return;
      queryClient.clear();
      void router.navigate({ to: '/sign-in' });
    };
    const queryClient: QueryClient = new QueryClient({
      queryCache: new QueryCache({ onError }),
      mutationCache: new MutationCache({ onError }),
      defaultOptions: {
        ...props.queryDefaults,
        queries: { refetchOnWindowFocus: false, ...props.queryDefaults?.queries },
      },
    });
    const router = createAppRouter({ api, queryClient }, props.history);
    return { api, queryClient, router };
  });
  return (
    <MantineProvider theme={theme} forceColorScheme="light">
      <QueryClientProvider client={deps.queryClient}>
        <ApiContext.Provider value={deps.api}>
          <RouterProvider router={deps.router} />
        </ApiContext.Provider>
      </QueryClientProvider>
    </MantineProvider>
  );
}
